import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Stack } from '@mui/material';
import { Button } from 'system/atoms';
import { Header } from 'common/ui/Header';
import { Campaign, Conversation } from 'generated-graphql/types';
import { TextBlock } from 'common/ui/TextBlock';
import { useInitializeCampaignWithParticipantsMutation } from 'generated-graphql/hooks/apollo-hooks';
import { Paths } from 'services/navigation/paths';
import { ToastType, useToast } from 'services/toast';
import { useLoadingOverlay } from 'services/loading/hooks';
import { getListOfParticipantsText } from 'modules/conversations/utils/getListOfParticipantsText';
import { ExpandableText } from 'common/ui/ExpandableText';
import { formatTemplatedText } from 'core/utils/formatTemplatedText';
import { CampaignType, Channels } from 'generated-graphql/graphql';
import { selectedParticipantsMapper } from 'modules/conversations/utils/selectedParticipantsMapper';
import { useCancelCampaign } from 'modules/campaigns/hooks/useCancelCampaign';

type LocationState = {
  title: string;
  customTemplatedMessage: string;
  conversations: Conversation[];
  campaign: Campaign;
};

export const CampaignCreationWithParticipantsSummary: React.FC = () => {
  const location = useLocation();

  const {
    title,
    customTemplatedMessage,
    conversations,
    campaign
  }: LocationState = location.state;

  const navigate = useNavigate();
  const { showToast } = useToast();
  const { showLoadingOverlay, hideLoadingOverlay } = useLoadingOverlay();
  const { showCancelCampaignModal } = useCancelCampaign({
    campaignId: campaign.id
  });

  const [mutate] = useInitializeCampaignWithParticipantsMutation({
    onCompleted: (data) => {
      showToast({
        message: 'Message to candidates are sending',
        type: ToastType.Success
      });

      hideLoadingOverlay();

      if (data?.initializeCampaignWithParticipants?.campaignId) {
        const campaignId = data.initializeCampaignWithParticipants.campaignId;
        navigate(`${Paths.OUTBOUND_CAMPAIGN}/${campaignId}`);
      }
    },
    onError: () => {
      showToast({
        message: 'Error sending message to candidates',
        type: ToastType.Error
      });

      hideLoadingOverlay();
    }
  });

  return (
    <Stack p={3}>
      <Header title={title} currentBreadcrumb="MESSAGE CANDIDATES" />
      <Stack spacing={3}>
        <TextBlock
          title="Recipients"
          subtitle={
            <Box width="70%">
              <ExpandableText text={getListOfParticipantsText(conversations)} />
            </Box>
          }
          variant="field"
        />

        <TextBlock
          title="Custom templated message"
          subtitle={formatTemplatedText(customTemplatedMessage)}
          variant="field"
        />

        <Stack direction="row" spacing={2}>
          <Button
            variant="primary"
            onClick={async () => {
              showLoadingOverlay();

              await mutate({
                variables: {
                  input: {
                    parentCampaignId: campaign.id,
                    campaignGroupId: campaign.campaignGroupId,
                    title: title,
                    customTemplatedMessage: customTemplatedMessage,
                    channel: Channels.Sms,
                    type: CampaignType.HumanOutreach,
                    jobDescriptionParsed: campaign.jobDescriptionParsed,
                    jobDescriptionText: campaign.jobDescriptionText,
                    outreachDescription: campaign.outreachDescription,
                    additionalContext: campaign.additionalContext,
                    closeOngoingConversations: true,
                    selectedParticipants:
                      selectedParticipantsMapper(conversations)
                  }
                }
              });
            }}
          >
            Send
          </Button>
          <Button
            variant="tertiary"
            onClick={() => {
              showCancelCampaignModal();
            }}
          >
            Cancel
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};
